import { gql } from "@apollo/client";

import { sharedUserFields, advisorUserFields, labelUserFields } from "../fragments/user";

const sharedProductUserFields = `
_id
firstName
lastName
email
`;

const sharedSuccessorObject = `
successors {
  address1
  address2
  city
  country
  createdAt
  ein
  email
  firstName
  lastName
  organizationName
  organizationNotes
  percentOfDaf
  phone
  relationship
  state
  status
  type
  updatedAt
  zip
  tier
  priority
  allocationType
  fixedAmount
  dateOfBirth
  minimumAge
  perStirpes
  trustee {
    name
    relationship
    email
    phone
  }
  distributionTiming
  distributionYears
  specialInstructions
}
`;

const sharedProductFields = `
_id
createdAt
friendlyId
name
productType
productStatus
closedAt
closeReason
reopenedAt
updatedAt
fundingAccount {
  ... on AccountAsset {
    _id
    accountLogin
    accountNumber
    assetName
    institution
  }
}
productAgreements {
  dateSigned
  signedBy
}
feeSchedule {
  _id
  feeName
  isDefault
  forProductType
  billingFrequency
  tiers { minAmount maxAmount feePercentage }
  yearlyMinimumFee
  adminFee { enabled method flatAmount }
  arrearFee { enabled method tierOffsetSource proRate }
  flatFee { enabled amount status startDate nextExecutionDate cancellationDate }
}
waiveFees
waiveFeeNotes
`;

const productObjectFields = `
... on DafProduct {
  ${sharedProductFields}
  waiveFeeReserve
  totals {
    cash
    stock
    alternativeAssets
    crypto
    other
    availableToGrant
    pendingGrantPayments
    ytdGranted
    totalGranted
    ytdContributed
    pendingContributions
    totalContributed
    balance
    estimatedFeeReserve
    uncalledCapital
  }
  ${sharedSuccessorObject}
}
... on ClatProduct {
  ${sharedProductFields}
  ${sharedSuccessorObject}
}
... on CommunityFundProduct {
  ${sharedProductFields}
  waiveFeeReserve
  totals {
    cash
    stock
    alternativeAssets
    crypto
    other
    availableToGrant
    pendingGrantPayments
    ytdGranted
    totalGranted
    ytdContributed
    pendingContributions
    totalContributed
    balance
    estimatedFeeReserve
    uncalledCapital
  }
  ${sharedSuccessorObject}
}
... on BrandingProduct {
  ${sharedProductFields}
}
`;

const userProductObjectFields = `
_id
associationType
agreementStatus
createdAt
product {
  ${productObjectFields}
}
`;

const sharedEntityFields = `
_id
friendlyId
address {
  address1
  address2
  city
  country
  state
  zip
}
entityName
phone
primaryEmail
entityType
`;

const QUERY_USER = gql`
  query User($sourcePath: String) {
    user(sourcePath: $sourcePath) {
      ... on InternalUser {
        ${sharedUserFields}
        products {
          ${userProductObjectFields}
        }
      }
      ... on DonorUser {
        advisor {
        _id
        firstName
        lastName
        entities {
          _id
          admin
          entity {
            ... on FirmEntity {
              _id
              entityName
            }
            ... on BranchEntity {
              _id
              entityName
            }
          }
        }
      }
        ${sharedUserFields}
        products {
          ${userProductObjectFields}
        }
      }
      ... on FirmUser {
        ${sharedUserFields}
        ${advisorUserFields}
        products {
          ${userProductObjectFields}
        }
        advisedUsers { # these are donor users
          ${sharedUserFields}
          products {
            ${userProductObjectFields}
          }
        }
        entities {
        admin
        entity {
          ... on PartnerEntity {
            _id
            ${sharedEntityFields}
          }
          ... on FirmEntity {
            crd
            ${sharedEntityFields}
            users {
              user {
                ... on FirmUser {
                  _id
                  role
                  firstName
                  lastName
                  totals {
                    date
                    aum
                    portfolioBalance
                    crypto
                    cash
                    stock
                    alternativeAssets
                    other
                  }
                  advisedUsers {
                    _id
                    firstName
                    lastName
                    totals {
                      date
                      portfolioBalance
                      crypto
                      cash
                      stock
                      alternativeAssets
                      other
                    }
                  }
                }
              }
            }
          }
          ... on BranchEntity {
            ${sharedEntityFields}
            firm {
              _id
              crd
            }
            users {
              user {
                ... on FirmUser {
                  _id
                  role
                  firstName
                  lastName
                  totals {
                    date
                    aum
                    portfolioBalance
                    crypto
                    cash
                    stock
                    alternativeAssets
                    other
                  }
                  advisedUsers {
                    _id
                    firstName
                    lastName
                    totals {
                      date
                      portfolioBalance
                      crypto
                      cash
                      stock
                      alternativeAssets
                      other
                    }
                  }
                }
              }
            }
          }
        }
      }
      }
      ... on EntityUser {
        ${sharedUserFields}
        products {
          ${userProductObjectFields}
        }
      }
      ... on ExternalUser {
        ${sharedUserFields}
      }
    }
  }
`;

const QUERY_USER_BY_ID = gql`
  query FindUserById($userId: ID!) {
    findUserById(userId: $userId) {
      ... on InternalUser {
        ${sharedUserFields}
        ${labelUserFields}
        products {
          ${userProductObjectFields}
        }
      }
      ... on DonorUser {
        advisor {
        _id
        firstName
        lastName
        meta {
          testData
        }
        entities {
          _id
          admin
          entity {
            ... on FirmEntity {
              _id
              entityName
            }
            ... on BranchEntity {
              _id
              entityName
            }
          }
        }
      }
        ${sharedUserFields}
        ${labelUserFields}
        products {
          ${userProductObjectFields}
        }
      }
      ... on FirmUser {
        ${sharedUserFields}
        ${advisorUserFields}
        ${labelUserFields}
        products {
          ${userProductObjectFields}
        }
        advisedUsers { # these are donor users
          ${sharedUserFields} 
          products {
            ${userProductObjectFields}
          }
        }
        entities {
          _id
          admin
          entity {
            ... on PartnerEntity {
              _id
              ${sharedEntityFields}
            }
            ... on FirmEntity {
              crd
              ${sharedEntityFields}
              users {
                admin
                user {
                  ... on FirmUser {
                    _id
                    role
                    firstName
                    lastName
                    totals {
                      date
                      aum
                      portfolioBalance
                      crypto
                      cash
                      stock
                      alternativeAssets
                      other
                    }
                    advisedUsers {
                      _id
                      firstName
                      lastName
                      totals {
                        date
                        portfolioBalance
                        crypto
                        cash
                        stock
                        alternativeAssets
                        other
                      }
                    }
                  }
                }
              }
            }
            ... on BranchEntity {
              ${sharedEntityFields}
              firm {
                _id
                crd
              }
              users {
                admin
                user {
                  ... on FirmUser {
                    _id
                    role
                    firstName
                    lastName
                    totals {
                      date
                      aum
                      portfolioBalance
                      crypto
                      cash
                      stock
                      alternativeAssets
                      other
                    }
                    advisedUsers {
                      _id
                      firstName
                      lastName
                      totals {
                        date
                        portfolioBalance
                        crypto
                        cash
                        stock
                        alternativeAssets
                        other
                      }
                    }
                  }
                }
              }
            }
          }
      }
      }
      ... on EntityUser {
        ${sharedUserFields}
        ${labelUserFields}
        products {
          ${userProductObjectFields}
        }
      }
      ... on ExternalUser {
        ${sharedUserFields}
        ${labelUserFields}
      }
    }
  }
`;

const FIND_ALL_USERS_WITHOUT_ADVISORS = gql`
  query FindAllUsersWithoutAdvisors($page: Int, $pageSize: Int) {
    findAllUsersWithoutAdvisors(page: $page, pageSize: $pageSize) {
      pageInfo {
        currentPage
        pageSize
        totalDocuments
        totalPages
      }
      users {
        ... on DonorUser {
          _id
          firstName
          lastName
        }
      }
    }
  }
`;

const FIND_ALL_USERS = gql`
  query FindAllUsers($page: Int, $pageSize: Int, $filters: UserServerFilterInput, $sort: SortInput) {
    findAllUsers(page: $page, pageSize: $pageSize, filters: $filters, sort: $sort) {
      pageInfo {
        currentPage
        pageSize
        totalDocuments
        totalPages
      }
      users {
        ... on InternalUser {
          _id
          firstName
          lastName
          email
          type
          role
          meta {
            status
            testData
          }
          leadFields {
            source
            status
            type
          }
          denormalized {
            advisorName
            firmName
            partnerName
          }
        }
        ... on DonorUser {
          _id
          firstName
          lastName
          email
          type
          role
          meta {
            status
            testData
          }
          leadFields {
            source
            status
            type
          }
          denormalized {
            advisorName
            firmName
            partnerName
          }
        }
        ... on FirmUser {
          _id
          firstName
          lastName
          email
          type
          role
          meta {
            status
            testData
          }
          leadFields {
            source
            status
            type
          }
          denormalized {
            advisorName
            firmName
            partnerName
          }
        }
        ... on EntityUser {
          _id
          firstName
          lastName
          email
          type
          meta {
            status
            testData
          }
          leadFields {
            source
            status
            type
          }
          denormalized {
            advisorName
            firmName
            partnerName
          }
        }
        ... on ExternalUser {
          _id
          firstName
          lastName
          email
          type
          meta {
            status
            testData
          }
          leadFields {
            source
            status
            type
          }
          denormalized {
            advisorName
            firmName
            partnerName
          }
        }
      }
    }
  }
`;

const FIND_USERS_SERVER_SIDE = gql`
  query FindUsersServerSide($input: ServerQueryInput) {
    findUsersServerSide(input: $input) {
      pageInfo {
        currentPage
        pageSize
        totalDocuments
        totalPages
      }
      users {
        ... on InternalUser {
          _id
          friendlyId
          firstName
          lastName
          email
          type
          role
          labels {
            _id
            name
            color
          }
          meta {
            status
            testData
          }
          leadFields {
            source
            status
            type
          }
          denormalized {
            advisorName
            firmName
            partnerName
          }
        }
        ... on DonorUser {
          _id
          friendlyId
          firstName
          lastName
          email
          type
          role
          labels {
            _id
            name
            color
          }
          meta {
            status
            testData
          }
          leadFields {
            source
            status
            type
          }
          denormalized {
            advisorName
            firmName
            partnerName
          }
        }
        ... on FirmUser {
          _id
          friendlyId
          firstName
          lastName
          email
          type
          role
          labels {
            _id
            name
            color
          }
          meta {
            status
            testData
          }
          leadFields {
            source
            status
            type
          }
          denormalized {
            advisorName
            firmName
            partnerName
          }
        }
        ... on EntityUser {
          _id
          friendlyId
          firstName
          lastName
          email
          type
          labels {
            _id
            name
            color
          }
          meta {
            status
            testData
          }
          leadFields {
            source
            status
            type
          }
          denormalized {
            advisorName
            firmName
            partnerName
          }
        }
        ... on ExternalUser {
          _id
          friendlyId
          firstName
          lastName
          email
          type
          labels {
            _id
            name
            color
          }
          meta {
            status
            testData
          }
          leadFields {
            source
            status
            type
          }
          denormalized {
            advisorName
            firmName
            partnerName
          }
        }
      }
    }
  }
`;

const FIND_LEAD_USERS = gql`
  query FindLeadUsers($page: Int, $pageSize: Int, $filters: UserServerFilterInput, $sort: SortInput) {
    findLeadUsers(page: $page, pageSize: $pageSize, filters: $filters, sort: $sort) {
      pageInfo {
        currentPage
        pageSize
        totalDocuments
        totalPages
      }
      users {
        ... on InternalUser {
          ${sharedUserFields}
        }
        ... on DonorUser {
          ${sharedUserFields}
        }
        ... on FirmUser {
          ${sharedUserFields}
        }
        ... on EntityUser {
          ${sharedUserFields}
        }
        ... on ExternalUser {
          ${sharedUserFields}
        }
      }
    }
  }
`;

const FIND_LEAD_USERS_SERVER_SIDE = gql`
  query FindLeadUsersServerSide($input: ServerQueryInput) {
    findLeadUsersServerSide(input: $input) {
      pageInfo {
        currentPage
        pageSize
        totalDocuments
        totalPages
      }
      users {
        ... on InternalUser {
          ${sharedUserFields}
        }
        ... on DonorUser {
          ${sharedUserFields}
        }
        ... on FirmUser {
          ${sharedUserFields}
        }
        ... on EntityUser {
          ${sharedUserFields}
        }
        ... on ExternalUser {
          ${sharedUserFields}
        }
      }
    }
  }
`;

const FIND_ONBOARDING_USERS = gql`
  query FindOnboardingUsers($onboardingStatus: OnboardingStatus) {
    findOnboardingUsers(onboardingStatus: $onboardingStatus) {
      ... on DonorUser {
        _id
        friendlyId
        firstName
        lastName
        createdAt
        email
        type
        role
        meta {
          onboardingStatus
          source
        }
        totals {
          portfolioBalance
        }
        advisor {
          _id
          firstName
          lastName
          entities {
            _id
            admin
            entity {
              ... on FirmEntity {
                _id
                entityName
                users {
                  _id
                  user {
                    ... on FirmUser {
                      _id
                      firstName
                      lastName
                      role
                    }
                  }
                }
              }
              ... on BranchEntity {
                _id
                entityName
                users {
                  _id
                  user {
                    ... on FirmUser {
                      _id
                      firstName
                      lastName
                      role
                    }
                  }
                }
              }
            }
          }
        }
        ownedProducts {
          _id
          product {
            ... on DafProduct {
              _id
              assets {
                ... on AccountAsset {
                  _id
                }
                ... on CryptoAsset {
                  _id
                }
                ... on EquityAsset {
                  _id
                }
                ... on FundAsset {
                  _id
                }
                ... on LendingAsset {
                  _id
                }
              }
            }
          }
        }
      }
      ... on FirmUser {
        _id
        friendlyId
        firstName
        lastName
        firmCrd
        email
        firmName
        createdAt
        type
        role
        meta {
          onboardingStatus
          source
        }
        entities {
          _id
          admin
          entity {
            ... on FirmEntity {
              _id
              entityName
            }
            ... on BranchEntity {
              _id
              entityName
            }
          }
        }
      }
    }
  }
`;

const USER_TOTALS = gql`
  query FindTotalUsers {
    findTotalUsers {
      advisors
      donors
    }
  }
`;

const FIND_FIRM_USER_METRICS = gql`
  query FindFirmUserMetrics($entityId: ID!, $entityType: EntityTypeEnum!) {
    findFirmUserMetrics(entityId: $entityId, entityType: $entityType) {
      aum
      totalClients
    }
  }
`;

const FIND_PARTNER_USERS = gql`
  query FindPartnerUsers($partnerId: ID!) {
    findPartnerUsers(partnerId: $partnerId) {
      _id
      firstName
      lastName
      email
      role
    }
  }
`;

const FIND_NON_DONOR_TYPE = gql`
  query Query($email: String!) {
    findNonDonorType(email: $email)
  }
`;

const FIND_DONOR_USERS = gql`
  query FindDonorUsers {
    findDonorUsers {
      ... on DonorUser {
      _id
      firstName
      lastName
      leadFields {
        type
      }
      }
    }
  }
`;

const FIND_ADVISORS = gql`
  query FindAdvisors {
    findAdvisors {
      ... on FirmUser {
        _id
        firstName
        lastName
        leadFields {
          type
        }
      }
    }
  }
`;

const FIND_INTERNAL_USERS = gql`
  query FindInternalUsers {
    findInternalUsers {
      _id
      firstName
      lastName
      role
    }
  }
`;

const FIND_FILTERED_USERS = gql`
  query FindFilteredUsers($args: UpdateUserInput!) {
    findFilteredUsers(args: $args) {
      ... on DonorUser {
        _id
        firstName
        lastName
        email
        type
        role
        meta {
          status
          testData
        }
      }
      ... on FirmUser {
        _id
        firstName
        lastName
        email
        type
        role
        meta {
          status
          testData
        }
      }
      ... on EntityUser {
        _id
        firstName
        lastName
        email
        type
        role
        meta {
          status
          testData
        }
      }
      ... on ExternalUser {
        _id
        firstName
        lastName
        email
        type
        role
        meta {
          status
          testData
        }
      }
      ... on InternalUser {
        _id
        firstName
        lastName
        email
        type
        role
        meta {
          status
          testData
        }
      }
    }
  }
`;

// Lightweight query for linking users to tasks
const FIND_USERS_FOR_LINKING = gql`
  query FindUsersForLinking($searchTerm: String, $limit: Int, $userType: String) {
    findUsersForLinking(searchTerm: $searchTerm, limit: $limit, userType: $userType) {
      ... on DonorUser {
        _id
        firstName
        lastName
        email
        type
        address { address1 address2 city state zip country }
        leadFields {
          isLead
          status
        }
        __typename
      }
      ... on FirmUser {
        _id
        firstName
        lastName
        email
        type
        role
        address { address1 address2 city state zip country }
        __typename
      }
      ... on InternalUser {
        _id
        firstName
        lastName
        email
        type
        role
        address { address1 address2 city state zip country }
        __typename
      }
      ... on EntityUser {
        _id
        firstName
        lastName
        email
        type
        address { address1 address2 city state zip country }
        __typename
      }
      ... on ExternalUser {
        _id
        firstName
        lastName
        email
        type
        address { address1 address2 city state zip country }
        leadFields {
          isLead
          status
        }
        __typename
      }
    }
  }
`;

export {
  QUERY_USER,
  QUERY_USER_BY_ID,
  FIND_ALL_USERS,
  FIND_LEAD_USERS,
  FIND_ONBOARDING_USERS,
  USER_TOTALS,
  FIND_ALL_USERS_WITHOUT_ADVISORS,
  FIND_FIRM_USER_METRICS,
  FIND_PARTNER_USERS,
  FIND_NON_DONOR_TYPE,
  FIND_DONOR_USERS,
  FIND_ADVISORS,
  FIND_INTERNAL_USERS,
  FIND_FILTERED_USERS,
  FIND_USERS_SERVER_SIDE,
  FIND_LEAD_USERS_SERVER_SIDE,
  FIND_USERS_FOR_LINKING
};
